/**
 * One product (2026-10-01): vision, the work delivering it (linked projects
 * and programs, with health), upcoming milestones, metrics with history,
 * and its ideas. Backend: app/services/products.py.
 */
import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { ArrowLeft, Plus, Trash2, X } from 'lucide-react'

import { Button } from '../components/ui/button'
import { IdeasTable } from '../components/product/IdeasTable'
import { HealthBadge, Sparkline, StageBadge } from '../components/product/productUi'
import { fmtValue } from '../components/product/format'
import { programsService } from '../services/programs'
import { projectsService } from '../services/projects'
import { STAGE_LABEL, productsService, type ProductDetail, type ProductMetric, type Stage } from '../services/products'
import { apiErrorMessage } from '../utils/apiError'
import { useProductRoles } from '../hooks/useProductRoles'

const input = 'min-h-[40px] rounded-md border border-border bg-background px-3 text-sm text-foreground'

export default function ProductDetailPage() {
  const { id } = useParams()
  const productId = Number(id)
  const navigate = useNavigate()
  const { canEdit, canSubmit } = useProductRoles()
  const [p, setP] = useState<ProductDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    setError(null)
    try {
      setP(await productsService.get(productId))
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load this product.'))
    }
  }, [productId])
  useEffect(() => { load() }, [load])

  const run = async (fn: () => Promise<ProductDetail | void>, ok?: string) => {
    setBusy(true)
    try {
      const res = await fn()
      if (res) setP(res)
      else await load()
      if (ok) toast.success(ok)
    } catch (err) {
      toast.error(apiErrorMessage(err, 'That didn’t work. Try again.'))
    } finally {
      setBusy(false)
    }
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {error} <Link to="/product" className="ml-2 underline">Back to products</Link>
        </div>
      </div>
    )
  }
  if (!p) return <p className="p-6 text-sm text-muted-foreground">Loading product…</p>

  const today = new Date().toISOString().slice(0, 10)
  const upcoming = p.milestones.filter((m) => !m.done && m.due_date >= today).slice(0, 6)

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 md:p-6">
      <Link to="/product" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Products
      </Link>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-foreground">{p.name}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {[p.owner_name && `Owner: ${p.owner_name}`, p.portfolio_name && `Portfolio: ${p.portfolio_name}`].filter(Boolean).join(' · ') || 'No owner set'}
          </p>
          {p.description && <p className="mt-2 max-w-3xl text-sm text-foreground">{p.description}</p>}
        </div>
        <div className="flex items-center gap-2">
          {canEdit ? (
            <select aria-label="Stage" value={p.stage} disabled={busy}
              onChange={(e) => run(() => productsService.update(p.id, { stage: e.target.value as Stage }), 'Stage updated')}
              className={input}>
              {(Object.keys(STAGE_LABEL) as Stage[]).map((s) => <option key={s} value={s}>{STAGE_LABEL[s]}</option>)}
            </select>
          ) : <StageBadge stage={p.stage} />}
          {canEdit && (
            <Button variant="outline" disabled={busy} aria-label="Delete product"
              onClick={() => {
                if (window.confirm(`Delete ${p.name}? Its ideas stay in the backlog; its metrics are removed.`)) {
                  run(async () => { await productsService.remove(p.id); navigate('/product') }, 'Product deleted')
                }
              }}>
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </header>

      {p.vision && (
        <section className="rounded-lg border border-border bg-card p-4">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Vision</h2>
          <p className="mt-1 text-foreground">{p.vision}</p>
        </section>
      )}

      <section aria-label="Delivery" className="space-y-3 rounded-lg border border-border bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-semibold text-foreground">Delivery</h2>
          {canEdit && <LinkPicker product={p} busy={busy} onLink={(type, eid) => run(() => productsService.link(p.id, type, eid), 'Linked')} />}
        </div>
        {p.projects.length === 0 ? (
          <p className="text-sm text-muted-foreground">No projects linked yet. Link the projects or programs that build this product.</p>
        ) : (
          <ul className="divide-y divide-border">
            {p.projects.map((x) => (
              <li key={x.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                <Link to={`/project/${x.id}`} className="font-medium text-foreground hover:underline">{x.name}</Link>
                <span className="flex items-center gap-2 text-muted-foreground">
                  <span>{x.status} · {x.progress}%</span>
                  {x.end_date && <span>ends {x.end_date}</span>}
                  <HealthBadge health={x.health} reason={x.health_reason} />
                </span>
              </li>
            ))}
          </ul>
        )}
        {p.links.length > 0 && (
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="text-muted-foreground">Linked:</span>
            {p.links.map((lk) => (
              <span key={`${lk.type}-${lk.id}`} className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-foreground">
                {lk.type === 'program' ? 'Program: ' : ''}{lk.name}
                {canEdit && (
                  <button type="button" aria-label={`Unlink ${lk.name}`} disabled={busy}
                    onClick={() => run(() => productsService.unlink(p.id, lk.type, lk.id), 'Unlinked')}
                    className="rounded-full p-0.5 hover:bg-background"><X className="h-3 w-3" /></button>
                )}
              </span>
            ))}
          </div>
        )}
        {upcoming.length > 0 && (
          <div>
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Coming up</h3>
            <ul className="space-y-1 text-sm">
              {upcoming.map((m) => (
                <li key={m.id} className="flex justify-between gap-2">
                  <span className="text-foreground">{m.name} <span className="text-muted-foreground">· {m.project_name}</span></span>
                  <span className="shrink-0 text-muted-foreground">{m.due_date}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <section aria-label="Metrics" className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-foreground">Metrics</h2>
        </div>
        {p.metrics.length === 0 && <p className="text-sm text-muted-foreground">No metrics yet. Track what tells you the product is working — active users, NPS, revenue, cycle time.</p>}
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {p.metrics.map((m) => (
            <MetricCard key={m.id} m={m} canEdit={canEdit} busy={busy}
              onRecord={(value, note) => run(() => productsService.recordValue(m.id, { value, note }), 'Value recorded')}
              onDelete={() => { if (window.confirm(`Delete the metric “${m.name}” and its history?`)) run(() => productsService.deleteMetric(m.id), 'Metric deleted') }} />
          ))}
        </div>
        {canEdit && <NewMetric busy={busy} onAdd={(body) => run(() => productsService.addMetric(p.id, body), 'Metric added')} />}
      </section>

      <section className="space-y-3">
        <h2 className="font-semibold text-foreground">Ideas</h2>
        <IdeasTable ideas={p.ideas} products={[{ id: p.id, name: p.name }]} fixedProductId={p.id}
          canTriage={canEdit} canSubmit={canSubmit} onChanged={load} />
      </section>
    </div>
  )
}

function LinkPicker({ product, busy, onLink }: { product: ProductDetail; busy: boolean; onLink: (t: 'project' | 'program', id: number) => void }) {
  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState<Array<{ key: string; label: string }>>([])
  const [choice, setChoice] = useState('')

  useEffect(() => {
    if (!open) return
    const linked = new Set(product.links.map((l) => `${l.type}:${l.id}`))
    Promise.all([projectsService.list(), programsService.list()])
      .then(([pj, pg]) => setOptions([
        ...pg.map((x) => ({ key: `program:${x.id}`, label: `Program: ${x.name}` })),
        ...pj.map((x) => ({ key: `project:${x.id}`, label: x.name })),
      ].filter((o) => !linked.has(o.key))))
      .catch(() => setOptions([]))
  }, [open, product.links])

  if (!open) return <Button variant="outline" onClick={() => setOpen(true)} className="min-h-[40px] gap-2"><Plus className="h-4 w-4" /> Link work</Button>
  return (
    <div className="flex flex-wrap items-center gap-2">
      <select aria-label="Project or program to link" value={choice} onChange={(e) => setChoice(e.target.value)} className={input}>
        <option value="">Choose a project or program…</option>
        {options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
      </select>
      <Button disabled={busy || !choice} onClick={() => {
        const [t, id] = choice.split(':')
        onLink(t as 'project' | 'program', Number(id))
        setChoice('')
        setOpen(false)
      }}>Link</Button>
      <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
    </div>
  )
}

function MetricCard({ m, canEdit, busy, onRecord, onDelete }: {
  m: ProductMetric; canEdit: boolean; busy: boolean; onRecord: (v: number, note: string | null) => void; onDelete: () => void
}) {
  const [val, setVal] = useState('')
  const values = (m.history ?? []).map((h) => h.value)
  return (
    <div className="space-y-2 rounded-lg border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm text-muted-foreground">{m.name}</div>
        {canEdit && (
          <button type="button" aria-label={`Delete ${m.name}`} onClick={onDelete} disabled={busy}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-destructive"><Trash2 className="h-3 w-3" /></button>
        )}
      </div>
      <div className="flex items-end justify-between gap-2">
        <div>
          <div className="text-2xl font-semibold text-foreground">{fmtValue(m.latest, m.unit)}</div>
          <div className="text-xs text-muted-foreground">
            {m.target !== null ? `Target ${m.direction === 'down' ? '≤' : '≥'} ${fmtValue(m.target, m.unit)}` : 'No target'}
            {m.on_target !== null && <span className={m.on_target ? ' text-success' : ' text-warning'}> · {m.on_target ? 'met' : 'not yet'}</span>}
          </div>
        </div>
        <Sparkline values={values} good={m.on_target} />
      </div>
      {m.latest_on && <div className="text-xs text-muted-foreground">Last updated {m.latest_on}</div>}
      {canEdit && (
        <form className="flex gap-2" onSubmit={(e) => {
          e.preventDefault()
          const v = Number(val)
          if (val.trim() === '' || Number.isNaN(v)) return
          onRecord(v, null)
          setVal('')
        }}>
          <input type="number" step="any" value={val} onChange={(e) => setVal(e.target.value)} placeholder="New value"
            aria-label={`New value for ${m.name}`} className={`${input} w-full`} />
          <Button type="submit" variant="outline" disabled={busy || val.trim() === ''}>Record</Button>
        </form>
      )}
    </div>
  )
}

function NewMetric({ busy, onAdd }: { busy: boolean; onAdd: (b: { name: string; unit: string | null; target: number | null; direction: 'up' | 'down' }) => void }) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [unit, setUnit] = useState('')
  const [target, setTarget] = useState('')
  const [direction, setDirection] = useState<'up' | 'down'>('up')
  if (!open) return <Button variant="outline" onClick={() => setOpen(true)} className="min-h-[40px] gap-2"><Plus className="h-4 w-4" /> Add metric</Button>
  return (
    <div className="grid gap-2 rounded-lg border border-border bg-card p-4 md:grid-cols-5">
      <input aria-label="Metric name" placeholder="e.g. Monthly active users" value={name} onChange={(e) => setName(e.target.value)} className={`${input} md:col-span-2`} />
      <input aria-label="Unit" placeholder="Unit (%, users, days)" value={unit} onChange={(e) => setUnit(e.target.value)} className={input} />
      <input aria-label="Target" type="number" step="any" placeholder="Target" value={target} onChange={(e) => setTarget(e.target.value)} className={input} />
      <select aria-label="Better when" value={direction} onChange={(e) => setDirection(e.target.value as 'up' | 'down')} className={input}>
        <option value="up">Higher is better</option><option value="down">Lower is better</option>
      </select>
      <div className="flex justify-end gap-2 md:col-span-5">
        <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
        <Button disabled={busy || !name.trim()} onClick={() => {
          onAdd({ name: name.trim(), unit: unit.trim() || null, target: target.trim() === '' ? null : Number(target), direction })
          setOpen(false); setName(''); setUnit(''); setTarget('')
        }}>Add metric</Button>
      </div>
    </div>
  )
}
